import React, { useState, useEffect, FormEvent, ChangeEvent, useCallback } from 'react';
import axiosInstance from '../api/commonAPI';
import { useParams, useNavigate } from 'react-router-dom';

interface Category {
    id: number;
    name: string;
    sortOrder: number;
}

const CategoryManagementPage: React.FC = () => {
    const { storeId } = useParams<{ storeId: string }>();
    const navigate = useNavigate();
    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);

    const fetchCategories = useCallback(async () => {
        if (!storeId) return;

        setIsLoading(true);
        try {
            const response = await axiosInstance.get<Category[]>(`/api/stores/${storeId}/categories`);
            setCategories(response.data);
        } catch (error) {
            console.error('카테고리 목록 조회 실패:', error);
            alert('카테고리 목록을 불러오지 못했습니다.');
        } finally {
            setIsLoading(false);
        }
    }, [storeId]);

    const handleAddCategory = async (e: FormEvent) => {
        e.preventDefault();
        if (!newCategoryName.trim()) {
            alert('카테고리 이름을 입력해 주세요.');
            return;
        }

        setIsLoading(true);
        try {
            await axiosInstance.post(`/api/stores/${storeId}/categories`, {
                name: newCategoryName.trim(),
            });
            alert('새 카테고리가 추가되었습니다.');
            setNewCategoryName('');
            fetchCategories();
        } catch (error) {
            console.error('카테고리 추가 실패:', error);
            alert('카테고리 추가 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteCategory = async (categoryId: number) => {
        if (!window.confirm('이 카테고리를 삭제할까요?')) {
            return;
        }

        setIsLoading(true);
        try {
            await axiosInstance.delete(`/api/stores/${storeId}/categories/${categoryId}`);
            alert('카테고리가 삭제되었습니다.');
            fetchCategories();
        } catch (error) {
            console.error('카테고리 삭제 실패:', error);
            alert('카테고리 삭제 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateCategory = async (e: FormEvent) => {
        e.preventDefault();
        if (!editingCategory || !editingCategory.name.trim()) {
            alert('카테고리 이름을 입력해 주세요.');
            return;
        }

        setIsLoading(true);
        try {
            await axiosInstance.put(
                `/api/stores/${storeId}/categories/${editingCategory.id}`,
                { name: editingCategory.name.trim() },
            );
            alert('카테고리가 수정되었습니다.');
            setEditingCategory(null);
            fetchCategories();
        } catch (error) {
            console.error('카테고리 수정 실패:', error);
            alert('카테고리 수정 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleMoveCategory = async (index: number, direction: -1 | 1) => {
        if (!storeId) return;

        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= categories.length) return;

        const previousCategories = [...categories];
        const nextCategories = [...categories];
        const [movedCategory] = nextCategories.splice(index, 1);
        nextCategories.splice(targetIndex, 0, movedCategory);
        setCategories(nextCategories);

        try {
            await axiosInstance.patch(`/api/stores/${storeId}/categories/order`, {
                ids: nextCategories.map(category => category.id),
            });
        } catch (error) {
            console.error('카테고리 정렬 변경 실패:', error);
            alert('카테고리 순서를 변경하지 못했습니다.');
            setCategories(previousCategories);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
            <h2>카테고리 관리</h2>
            <button
                onClick={() => navigate('/')}
                style={{ ...buttonStyle, backgroundColor: '#6c757d', padding: '8px 15px' }}
            >
                뒤로가기
            </button>

            {editingCategory && (
                <form onSubmit={handleUpdateCategory} style={formStyle}>
                    <h3>카테고리 수정</h3>
                    <input
                        type="text"
                        value={editingCategory.name}
                        onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                        style={{ width: '100%', padding: '8px', marginBottom: '10px' }}
                    />
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            type="submit"
                            disabled={isLoading}
                            style={{ ...buttonStyle, backgroundColor: '#ffc107', color: '#111' }}
                        >
                            수정 완료
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditingCategory(null)}
                            style={{ ...buttonStyle, backgroundColor: '#6c757d' }}
                        >
                            취소
                        </button>
                    </div>
                </form>
            )}

            {!editingCategory && (
                <form onSubmit={handleAddCategory} style={formStyle}>
                    <h3>새 카테고리 추가</h3>
                    <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="newCategoryName">카테고리 이름</label>
                        <input
                            id="newCategoryName"
                            type="text"
                            value={newCategoryName}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setNewCategoryName(e.target.value)}
                            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px'
                        }}
                    >
                        {isLoading ? '추가 중...' : '카테고리 추가'}
                    </button>
                </form>
            )}

            <div>
                <h3>등록된 카테고리 목록</h3>
                <ul style={{ listStyleType: 'none', padding: 0 }}>
                    {isLoading && <p>불러오는 중...</p>}
                    {!isLoading && categories.length > 0 ? (
                        categories.map((category, index) => (
                            <li
                                key={category.id}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '10px',
                                    borderBottom: '1px solid #eee'
                                }}
                            >
                                <span style={{ fontWeight: 700 }}>{category.name}</span>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        type="button"
                                        onClick={() => handleMoveCategory(index, -1)}
                                        disabled={index === 0}
                                        style={{ ...buttonStyle, backgroundColor: '#475569' }}
                                    >
                                        위
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleMoveCategory(index, 1)}
                                        disabled={index === categories.length - 1}
                                        style={{ ...buttonStyle, backgroundColor: '#475569' }}
                                    >
                                        아래
                                    </button>
                                    <button
                                        onClick={() => setEditingCategory({ ...category })}
                                        style={{ ...buttonStyle, backgroundColor: '#007bff' }}
                                    >
                                        수정
                                    </button>
                                    <button
                                        onClick={() => handleDeleteCategory(category.id)}
                                        style={{ ...buttonStyle, backgroundColor: '#dc3545' }}
                                    >
                                        삭제
                                    </button>
                                </div>
                            </li>
                        ))
                    ) : (
                        !isLoading && <p>등록된 카테고리가 없습니다.</p>
                    )}
                </ul>
            </div>
        </div>
    );
};

const formStyle: React.CSSProperties = {
    marginBottom: '30px',
    padding: '20px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    backgroundColor: '#f9f9f9'
};

const buttonStyle: React.CSSProperties = {
    padding: '5px 10px',
    fontSize: '14px',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
};

export default CategoryManagementPage;
